import type { PurchaseType } from '../types'

/**
 * STEP 1 (Phase 9) — production API base URL.
 *
 * Local dev: `VITE_API_BASE_URL` is unset → same-origin `/api` (Vite proxies
 * it to the local Express backend, so localhost keeps working unchanged).
 *
 * Production (Cloudflare Pages → Workers): Pages serves static files only, so
 * the Worker origin must be explicit, e.g.
 * `VITE_API_BASE_URL=https://warung-grosir-api.<subdomain>.workers.dev/api`
 * configured in the Pages dashboard — never hardcoded here.
 */
function resolveApiBase(): string {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim().replace(/\/+$/, '')
  return configured && configured.length > 0 ? configured : '/api'
}

export const API_BASE_URL = resolveApiBase()

const API_BASE = API_BASE_URL

async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('admin_token')
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Request failed')
  }

  return data
}

async function publicApiRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Request failed')
  }

  return data
}

export const productApi = {
  getAll: () => apiRequest<{ success: boolean; data: unknown[] }>('/products/admin'),
  getById: (id: number) => apiRequest<{ success: boolean; data: unknown }>(`/products/admin/${id}`),
  create: (data: unknown) => apiRequest<{ success: boolean; data: unknown }>('/products/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: unknown) => apiRequest<{ success: boolean; data: unknown }>(`/products/admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  toggleActive: (id: number) => apiRequest<{ success: boolean; data: unknown }>(`/products/admin/${id}/active`, {
    method: 'PATCH',
  }),
  remove: (id: number) => apiRequest<{ success: boolean; data: unknown }>(`/products/admin/${id}`, {
    method: 'DELETE',
  }),
}

export const clientProductApi = {
  getAll: () => publicApiRequest<{ success: boolean; data: unknown[] }>('/products'),
}

export const orderApi = {
  getAll: () => apiRequest<{ success: boolean; data: unknown[] }>('/orders/admin'),
  getById: (id: number) => apiRequest<{ success: boolean; data: unknown }>(`/orders/admin/${id}`),
}

/**
 * Phase 8A-4 — admin dashboard statistics (authenticated, read-only).
 * All counts and sums are computed by the backend from real database rows.
 */
export interface DashboardRecentOrder {
  id: number
  orderNumber: string
  outletName: string
  totalAmount: number
  status: string
  createdAt: string
}

export interface DashboardStatsData {
  activeProductCount: number
  totalProductCount: number
  todayOrderCount: number
  todayTransactionAmount: number
  totalTransactionAmount: number
  recentOrders: DashboardRecentOrder[]
}

export const dashboardApi = {
  getStats: () =>
    apiRequest<{ success: boolean; data: DashboardStatsData }>('/admin/dashboard'),
}

/**
 * Phase 7B-1 — public client order submission.
 *
 * Sends ONLY the minimal payload the backend accepts as authoritative input:
 * outlet name + product_id / purchase_type / quantity per item.
 * Prices, subtotals, totals and order numbers are NEVER sent: the backend
 * recalculates everything from the database and returns the real order.
 */
export interface CreateOrderItemPayload {
  product_id: number
  purchase_type: PurchaseType
  quantity: number
}

export interface CreateOrderPayload {
  outlet_name: string
  items: CreateOrderItemPayload[]
}

export interface CreatedOrderItem {
  productId: number
  productName: string
  purchaseType: PurchaseType
  quantity: number
  unitPrice: number
  subtotal: number
}

export interface CreatedOrder {
  id: number
  orderNumber: string
  outlet: { id: number; name: string }
  status: string
  items: CreatedOrderItem[]
  total: number
  createdAt: string
}

export const clientOrderApi = {
  create: (payload: CreateOrderPayload) =>
    publicApiRequest<{ success: boolean; data: CreatedOrder }>('/orders', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}